import { HTMLAttributes, RefObject, SyntheticEvent, useEffect, useState } from 'react';
import { AutocompleteRenderInputParams, Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';
import { TypographyHighlighter } from '@components/typography/TypographyHighlighter';
import { TextField } from '@components/textField';
import { useValidPhysicalAddress } from '@services/validation';
import { useAddressSuggestions } from '@services/smarty';
import { InputValidation } from '@pages/NewQuote/location/InputValidation';
import { ToastType } from '@components/toast/ToastType.enum';
import { ExternalIcon } from '@pages/NewQuote/location/ExternalIcon';
import { EnteredAddress } from '@models/validation';
import { Autocomplete, AutocompletePaper, quotePanelLabel as quotePanelLabelStyles } from '@components/autocomplete';
import { formatAddress, areAddressesEqual, initialAddressState } from '@utils/address';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';

export interface AddressProps {
  label: string;
  address?: EnteredAddress;
  onChangeAddressEntry: () => void;
  setIsLoading?: (l: boolean) => void;
  onSelectAddress: (a: EnteredAddress) => void;
  /** Semicolon-separated state abbreviations forwarded to the address suggestion API to restrict results */
  filteredJurisdictions?: string;
  /** Shows "Location already exists on quote" error on the input */
  repeatedLocationError?: boolean;
  /** When provided, validates that the selected address state is in the list */
  stateList?: string[];
  /** Controls whether the validation adornment and state-error message are shown; default true */
  shouldValidate?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
}

export const Address = ({
  address,
  label,
  onChangeAddressEntry,
  setIsLoading,
  onSelectAddress,
  filteredJurisdictions,
  repeatedLocationError = false,
  stateList,
  shouldValidate = true,
  inputRef,
}: AddressProps) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const initialSelectedValue =
    address && address.street1 && address.state && address.city && address.zipCode ? address : null;
  const [selectedValue, setSelectedValue] = useState<EnteredAddress | null>(initialSelectedValue);
  const [inputValue, setInputValue] = useState<string>('');

  const { addressSuggestions } = useAddressSuggestions(inputValue, filteredJurisdictions);
  const { isFetching, validPhysicalAddress, refetch, error } = useValidPhysicalAddress(
    selectedValue || initialAddressState,
  );
  const validationStatus = validPhysicalAddress?.status;
  const isStateValid = stateList && address?.state ? stateList.includes(address.state) : true;
  const [hasValidated, setHasValidated] = useState(false);

  const debouncedInputValue = useDebouncedCallback((value) => {
    setInputValue(value);
  }, DEBOUNCE_DELAYS.TYPING);

  useEffect(() => {
    if (selectedValue && !validationStatus && !isFetching && !error) {
      refetch();
    }
    if (stateList !== undefined && selectedValue && validationStatus && !isFetching) {
      // Guard against empty stateList during initial loading to avoid a flash of the error state
      if (stateList.length === 0) return;
      setHasValidated(true);
    }
  }, [selectedValue, validationStatus, refetch, isFetching, stateList, error]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, {
        persist: true,
        variant: ToastType.Error,
      });
    }
    setIsLoading?.(isFetching || !!error);
  }, [isFetching, error, setIsLoading, enqueueSnackbar]);

  const handleChange = (event: SyntheticEvent, value: EnteredAddress | null) => {
    setSelectedValue(value);
    if (!value) return;
    onSelectAddress(value);
  };

  const handleInputChange = (event: SyntheticEvent, value: string) => {
    debouncedInputValue(value);
  };

  const handleValidationRefresh = async () => {
    await refetch();
    closeSnackbar();
  };

  return (
    <Autocomplete
      fullWidth
      options={addressSuggestions}
      filterOptions={(options) => options}
      getOptionLabel={(v) => formatAddress(v as EnteredAddress)}
      isOptionEqualToValue={areAddressesEqual}
      value={selectedValue || null}
      disableClearable={!!selectedValue}
      forcePopupIcon={false}
      onChange={(e, v) => handleChange(e, v as EnteredAddress | null)}
      onInputChange={handleInputChange}
      blurOnSelect
      noOptionsText={
        <Typography variant='bodySmall' sx={{ color: 'common.black' }}>
          {inputValue.length ? "We can't find a matching address" : "Begin typing and we'll provide some suggestions."}
        </Typography>
      }
      slots={{
        paper: (props: HTMLAttributes<HTMLElement>) => (
          <AutocompletePaper
            textButton='Manually enter an address'
            textButtonOnClick={onChangeAddressEntry}
            {...props}
          />
        ),
      }}
      renderOption={(props, option: EnteredAddress) => (
        <li {...props} key={props.key}>
          <TypographyHighlighter
            text={formatAddress(option)}
            variant='bodySmall'
            color='common.black'
            highlightText={inputValue}
            highlightStyle={{
              fontWeight: 700,
              backgroundColor: 'transparent',
            }}
          />
        </li>
      )}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Typography
              variant='caption'
              component='label'
              aria-label={label}
              sx={{
                color: repeatedLocationError || !!error ? 'error.main' : 'text.main',
                ...quotePanelLabelStyles,
              }}
            >
              {label}
            </Typography>
          </Box>
          <TextField
            {...params}
            inputRef={inputRef}
            error={repeatedLocationError}
            helperText={repeatedLocationError ? 'Location already exists on quote' : undefined}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                ...params.InputProps,
                endAdornment:
                  shouldValidate && selectedValue ? (
                    <InputValidation
                      loading={isFetching}
                      error={error}
                      isStateValid={isStateValid === false ? false : undefined}
                      status={validationStatus}
                    />
                  ) : null,
              },
            }}
            externalIcon={<ExternalIcon isError={!!error} onRefresh={handleValidationRefresh} />}
          />
          {shouldValidate && stateList !== undefined && hasValidated && isStateValid === false && (
            <Typography
              variant='bodySmall'
              fontWeight='bold'
              sx={{ display: 'block', mt: '4px', textAlign: 'center', color: 'error.main' }}
            >
              This jurisdiction is not yet available. Please delete or change the address.
            </Typography>
          )}
        </>
      )}
    />
  );
};
